import type { SortingState } from "@tanstack/react-table";

/** The sort part of a view config's `columns`: each column's asc and desc sort values, by column id. */
export type ViewSortColumns<TSort extends string> = Partial<
    Record<string, { sort?: { asc: TSort; desc: TSort } }>
>;

/** One DataGrid sort entry for a saved sort; [] when no column sorts by it. */
export const toViewSorting = <TSort extends string>({
    sort,
    columns,
}: {
    sort: TSort;
    columns: ViewSortColumns<TSort>;
}): SortingState => {
    for (const [id, column] of Object.entries(columns)) {
        if (column?.sort?.asc === sort) {
            return [{ id, desc: false }];
        }
        if (column?.sort?.desc === sort) {
            return [{ id, desc: true }];
        }
    }
    return [];
};

/** The saved sort for the first sort entry; undefined when that column doesn't sort. */
export const toViewSortValue = <TSort extends string>({
    sorting,
    columns,
}: {
    sorting: SortingState;
    columns: ViewSortColumns<TSort>;
}): TSort | undefined => {
    const [firstSort] = sorting;
    if (!firstSort) {
        return undefined;
    }
    const columnSort = columns[firstSort.id]?.sort;
    if (!columnSort) {
        return undefined;
    }
    return firstSort.desc ? columnSort.desc : columnSort.asc;
};
